import React, { useEffect } from 'react';
import { Radio, TextField } from '@iclinic/design-system';
import { useFormikContext } from 'formik';
import { useRifm } from 'rifm';
import ReactInputMask from 'react-input-mask';
import { TextFieldProps } from '@iclinic/design-system/src/components/TextField';

import * as S from './AddressForm.styles';
import BRAZIL_STATES_INITIALS from 'shared/constants/brStates';
import { BillingInfo } from 'features/checkout/state/types';
import {
  cepFormatter,
  cnpjFormatter,
  cpfFormatter,
} from 'shared/utils/formatters';
import { fetchZipCode } from 'features/checkout/services/getZipCodeAddress';
import { AddressField } from './AddressField';
import { onlyLetters } from './validation';

const PERSON_TYPE_PF = 'pf';
const PERSON_TYPE_PJ = 'pj';

const AddressForm = () => {
  const {
    values,
    handleChange,
    touched,
    errors,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext<BillingInfo>();

  const cepGridArea =
    values.person_type === PERSON_TYPE_PF
      ? 'cep'
      : 'cep / cep / birthdate / birthdate';

  const handlePersonTypeChange = (
    value: typeof PERSON_TYPE_PF | typeof PERSON_TYPE_PJ,
  ) => {
    setFieldValue('person_type', value);
    setFieldValue('cpf_cnpj', '');
    setFieldValue('responsible', '');

    setFieldError('responsible', '');
    setFieldTouched('responsible', false, false);
    setFieldError('cpf_cnpj', '');
    setFieldTouched('cpf_cnpj', false, false);
  };

  const documentMask = useRifm({
    value: values.cpf_cnpj,
    onChange: (value: string) => setFieldValue('cpf_cnpj', value),
    format:
      values.person_type === PERSON_TYPE_PJ ? cnpjFormatter : cpfFormatter,
  });

  const zipCodeMaskForms = useRifm({
    value: values.billing_zip,
    onChange: (value: string) => setFieldValue('billing_zip', value),
    format: cepFormatter,
  });

  const handleResponsibleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueFilter = onlyLetters(e.target.value);
    setFieldValue('responsible', valueFilter);
  };

  useEffect(() => {
    const cleanCep = values.billing_zip?.replace(/\D/g, '');
    if (cleanCep && cleanCep.length === 8) {
      fetchZipCode(values.billing_zip).then((data) => {
        if (data && !data.error) {
          setFieldValue('billing_address', data.street ?? '');
          setFieldValue('billing_neighborhood', data.neighborhood ?? '');
          setFieldValue('billing_city', data.city ?? '');
          setFieldValue('billing_state', data.state ?? '');
        }
      });
    }
  }, [values.billing_zip, setFieldValue]);

  return (
    <S.Root>
      <S.FieldsWrapper>
        <S.RadioWrapper>
          <Radio
            color="primary"
            name="plan-option"
            label="Pessoa Física"
            checked={values.person_type === PERSON_TYPE_PF}
            onChange={() => handlePersonTypeChange(PERSON_TYPE_PF)}
          />

          <Radio
            color="primary"
            name="plan-option"
            label="Pessoa Jurídica"
            checked={values.person_type === PERSON_TYPE_PJ}
            onChange={() => handlePersonTypeChange(PERSON_TYPE_PJ)}
          />
        </S.RadioWrapper>

        <TextField
          id="cpf_cnpj"
          label={values.person_type === PERSON_TYPE_PJ ? 'CNPJ' : 'CPF'}
          name="cpf_cnpj"
          placeholder=""
          value={documentMask.value}
          onChange={documentMask.onChange}
          error={touched.cpf_cnpj && !!errors.cpf_cnpj}
          helperText={touched.cpf_cnpj && errors.cpf_cnpj}
          style={{ gridArea: 'document' }}
        />

        <TextField
          id="responsible"
          label={
            values.person_type === PERSON_TYPE_PJ ? 'Razão Social' : 'Nome'
          }
          name="responsible"
          value={values.responsible}
          onChange={handleResponsibleChange}
          error={touched.responsible && !!errors.responsible}
          helperText={touched.responsible && errors.responsible}
          style={{ gridArea: 'name' }}
        />

        <TextField
          id="billing_zip"
          label="CEP"
          name="billing_zip"
          placeholder="12345-678"
          value={zipCodeMaskForms.value}
          onChange={zipCodeMaskForms.onChange}
          error={touched.billing_zip && !!errors.billing_zip}
          helperText={touched.billing_zip && errors.billing_zip}
          style={{
            gridArea: cepGridArea,
          }}
        />

        {values.person_type === PERSON_TYPE_PF && (
          <ReactInputMask
            mask="99/99/9999"
            value={values.billing_birthdate ?? ''}
            onChange={handleChange}
            maskChar=""
          >
            {(inputProps: TextFieldProps) => (
              <TextField
                {...inputProps}
                id="billing_birthdate"
                label="Data de Nascimento"
                name="billing_birthdate"
                error={touched.billing_birthdate && !!errors.billing_birthdate}
                helperText={
                  touched.billing_birthdate && errors.billing_birthdate
                }
                placeholder="DD/MM/AAAA"
                style={{ gridArea: 'birthdate' }}
              />
            )}
          </ReactInputMask>
        )}

        <AddressField
          id="address"
          name="billing_address"
          label="Endereço"
          placeholder="Rua das Flores"
          gridArea="address"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <AddressField
          id="number"
          name="billing_number"
          label="Número"
          placeholder="55"
          gridArea="number"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <AddressField
          id="billing_address2"
          label="Complemento"
          name="billing_address2"
          placeholder="Apartamento 123, Bloco A"
          values={values}
          handleChange={handleChange}
          errors={errors}
          touched={touched}
          gridArea="complement"
        />

        <AddressField
          id="neighborhood"
          name="billing_neighborhood"
          label="Bairro"
          placeholder="Centro"
          gridArea="neighborhood"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <AddressField
          id="city"
          name="billing_city"
          label="Cidade"
          placeholder="São Paulo"
          gridArea="city"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <TextField
          id="billing_state"
          label="Estado"
          name="billing_state"
          value={values.billing_state}
          onChange={handleChange}
          error={touched.billing_state && !!errors.billing_state}
          helperText={touched.billing_state && errors.billing_state}
          select
          SelectProps={{
            native: true,
          }}
          style={{ gridArea: 'state' }}
        >
          <option value="">Selecione</option>
          {BRAZIL_STATES_INITIALS.map((state) => (
            <option value={state} key={state}>
              {state}
            </option>
          ))}
        </TextField>
      </S.FieldsWrapper>
    </S.Root>
  );
};

export default AddressForm;
